import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Loader2 } from "lucide-react";

export default function AdminImport() {
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [cacheImages, setCacheImages] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const run = async () => {
    if (!file) return toast.error("Choose a Shopify products.csv first.");
    setRunning(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams();
      params.set("dry_run", dryRun ? "true" : "false");
      params.set("cache_images", cacheImages ? "true" : "false");
      const { data } = await api.post(`/admin/import/shopify?${params.toString()}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setResult(data);
      toast.success(dryRun ? "Dry run complete." : `Imported ${data.imported} · Updated ${data.updated}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed.");
    } finally { setRunning(false); }
  };

  return (
    <div data-testid="admin-import">
      <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B]">Data</p>
      <h1 className="font-serif text-4xl mb-8">Shopify Product Import</h1>

      <div className="bg-white border border-black/5 p-8 max-w-2xl">
        <p className="text-sm text-[#4A4A4A] leading-relaxed mb-6">
          Upload a <span className="font-mono-alt">products.csv</span> exported from your Shopify admin (Products → Export).
          Rows are grouped by <span className="font-mono-alt">Handle</span> — each becomes a product with its images and size variants.
          Occasion and craft are inferred from your Shopify tags.
        </p>

        <div
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-[#BCA37F]/60 hover:border-[#5C1A1B] transition-colors cursor-pointer bg-[#F3EFEA]/30 p-8 text-center flex flex-col items-center gap-2"
          data-testid="import-dropzone"
        >
          <UploadCloud size={22} className="text-[#5C1A1B]" strokeWidth={1.4} />
          <p className="text-sm">{file ? file.name : "Click to choose products.csv"}</p>
          <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-input" />
        </div>

        <label className="mt-6 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} data-testid="import-dry-run" />
          Dry run first (recommended — parses the file without changing your catalog)
        </label>
        <label className="mt-2 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={cacheImages} onChange={(e) => setCacheImages(e.target.checked)} data-testid="import-cache-images" />
          Cache product images to our storage (bulk-download from Shopify CDN — slower, but the store becomes fully self-hosted)
        </label>

        <button onClick={run} disabled={running || !file} className="mt-6 btn-editorial disabled:opacity-40" data-testid="import-run">
          {running ? <Loader2 className="animate-spin inline" size={14} /> : null}
          {running ? " Running…" : dryRun ? "Run dry-run" : "Import to catalog"}
        </button>
      </div>

      {result && (
        <div className="bg-white border border-black/5 p-8 mt-6 max-w-2xl" data-testid="import-result">
          <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B] mb-3">Result</p>
          <ul className="text-sm space-y-1">
            <li>Total handles found: <span className="font-mono-alt">{result.total_handles}</span></li>
            <li>Would be imported (new): <span className="font-mono-alt">{result.imported}</span></li>
            <li>Would be updated (existing slug): <span className="font-mono-alt">{result.updated}</span></li>
            <li>Skipped: <span className="font-mono-alt">{result.skipped}</span></li>
            {typeof result.images_cached === "number" && (
              <li>Images cached to storage: <span className="font-mono-alt">{result.images_cached}</span></li>
            )}
            <li>Mode: <span className="font-mono-alt">{result.dry_run ? "dry run" : "live"}{result.cache_images ? " · caching images" : ""}</span></li>
          </ul>
          {result.errors?.length > 0 && (
            <>
              <p className="text-[11px] uppercase tracking-nav text-[#D32F2F] mt-4 mb-2">First errors</p>
              <ul className="text-xs font-mono-alt text-[#D32F2F] space-y-1">
                {result.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
